import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/Dialog';
import { Button } from '../../ui/Button';
import { Spinner } from '../Spinner';
import { cn } from '@hero/web-lib';
import { Check } from 'lucide-react';

const EXIT_ANIMATION_MS = 250;

export interface ModalFormProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Called after the close animation finishes. Use to clear "exiting" state so the modal can unmount. */
  onExited?: () => void;
  /** Modal title */
  title: string;
  /** Optional modal description */
  description?: string;
  /** Branded app header (logo + name) rendered above the title. */
  headerBrand?: ReactNode;
  /** Form content */
  children: ReactNode;
  /** Called when Save button is clicked; run validation and save from here (no form submit). */
  onSave?: () => void | Promise<void>;
  /** Loading state - disables form and shows spinner (e.g. save in progress) */
  isLoading?: boolean;
  /** Loading initial form data (e.g. fetching entity by id for edit). When true, shows loading indicator in form area. */
  isLoadingForm?: boolean;
  /** Success state - auto-closes modal */
  isSuccess?: boolean;
  /** Cancel button text */
  cancelText?: string;
  /**
   * Save button label. A node, not just a string, so a label can carry an
   * amount rendered by `<Price>` — "Save {glyph} 1,000" has to use the same
   * currency glyph as the rest of the app, and that can't come out of a
   * template string.
   */
  saveText?: ReactNode;
  /** Allow closing via outside click (default: true) */
  allowOutsideClickClose?: boolean;
  /** Custom className for dialog content */
  className?: string;
  /** Hide footer buttons (for custom footers) */
  hideFooter?: boolean;
  /** Custom footer content */
  customFooter?: ReactNode;
  /** Disable save button */
  disableSave?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Hide the X close button (default: true for forms) */
  hideCloseButton?: boolean;
}

/**
 * Modal size classes - designed for proper modal widths
 * sm:   400px - Small dialogs (confirmations, simple forms)
 * md:   500px - Medium dialogs (standard forms)
 * lg:   640px - Large dialogs (complex forms)
 * xl:   768px - Extra large dialogs (forms with maps, rich content)
 * 2xl:  900px - 2X large dialogs (full-featured modals)
 * full: 100%  - Full width (rare, for very complex UIs)
 */
const sizeClasses = {
  sm: 'max-w-[400px]',
  md: 'max-w-[500px]',
  lg: 'max-w-[640px]',
  xl: 'max-w-[768px]',
  '2xl': 'max-w-[900px]',
  full: 'max-w-full',
};

/**
 * ModalForm - Reusable modal component for forms
 *
 * Features:
 * - Consistent Cancel/Save buttons in footer
 * - No X close button (use Cancel button instead)
 * - Loading state with spinner
 * - Auto-close on success
 * - Disabled state during submission
 * - RTL support
 * - Accessible (Escape key, outside click)
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const { mutate, isLoading, isSuccess } = useMutation(...);
 *
 * <ModalForm
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Create Customer"
 *   onSave={() => formHook.form.handleSubmit(formHook.onValidSubmit)()}
 *   isLoading={isLoading}
 *   isSuccess={isSuccess}
 * >
 *   <CustomerForm />
 * </ModalForm>
 * ```
 */
export function ModalForm({
  isOpen,
  onClose,
  onExited,
  title,
  description,
  headerBrand,
  children,
  onSave,
  isLoading = false,
  isLoadingForm = false,
  isSuccess = false,
  cancelText,
  saveText,
  allowOutsideClickClose = true,
  className,
  hideFooter = false,
  customFooter,
  disableSave = false,
  size = 'md',
  hideCloseButton = true,
}: ModalFormProps) {
  const { t, i18n } = useTranslation(['c-main']);
  const isRTL = i18n.language === 'ar';

  // Keep children mounted during exit animation so content animates out smoothly; unmount after
  const [isExiting, setIsExiting] = React.useState(false);
  const prevOpenRef = React.useRef(isOpen);
  React.useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      setIsExiting(true);
    }
    if (isOpen) {
      setIsExiting(false);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  React.useEffect(() => {
    if (!isExiting) return;
    const timer = setTimeout(() => {
      setIsExiting(false);
      onExited?.();
    }, EXIT_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isExiting, onExited]);

  const showContent = isOpen || isExiting;

  const handleSaveClick = () => {
    onSave?.();
  };

  // Auto-close on success
  React.useEffect(() => {
    if (isSuccess) {
      // Small delay to show success state
      const timer = setTimeout(() => {
        onClose();
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isSuccess, onClose]);

  const handleOpenChange = (open: boolean) => {
    // Only allow closing via outside click if allowed and not loading
    if (!open && allowOutsideClickClose && !isLoading && !isLoadingForm) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          // Flex column: header + scrollable body + fixed footer. Maps (iframes) bleed
          // through sticky footers inside overflow-y-auto containers — keep the footer
          // outside the scroll region instead.
          'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 [scrollbar-gutter:stable]',
          sizeClasses[size],
          hideCloseButton && '[&>button]:hidden',
          className,
        )}
        onPointerDownOutside={e => {
          if (!allowOutsideClickClose || isLoading || isLoadingForm) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={e => {
          // Allow Escape key to close even during loading (for accessibility)
          if (isLoading || isLoadingForm) {
            e.preventDefault();
          } else {
            onClose();
          }
        }}
        onInteractOutside={e => {
          if (isLoading || isLoadingForm) {
            e.preventDefault();
          }
        }}>
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 bg-background px-6 pb-2 pt-6">
          {headerBrand ? <div className="mb-4">{headerBrand}</div> : null}
          <DialogTitle className={isRTL ? 'text-right' : 'text-left'}>{title}</DialogTitle>
          {description && (
            <DialogDescription className={cn('mt-0.5', isRTL ? 'text-right' : 'text-left')}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Scrollable body — maps and long forms stay contained above the footer */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-2">
          <fieldset disabled={isLoading || isLoadingForm} className="m-0 space-y-4 border-0 p-0">
            {showContent ? (
              isLoadingForm && isOpen ? (
                <div className="flex min-h-[120px] items-center justify-center" aria-busy="true">
                  <Spinner className="h-8 w-8" />
                </div>
              ) : (
                <>{children}</>
              )
            ) : null}
          </fieldset>
        </div>

        {!hideFooter && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-background px-6 py-4">
            <DialogFooter className={cn('gap-2', isRTL && 'flex-row-reverse')}>
              {customFooter || (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isLoading || isLoadingForm}
                    className={cn('min-w-[100px]', isRTL ? 'ml-2' : 'mr-2')}>
                    {cancelText || t('c-main:crud_buttons.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveClick}
                    disabled={isLoading || isLoadingForm || disableSave || isSuccess}
                    className="min-w-[100px]">
                    <Check className="size-4" />
                    {isLoading ? (
                      <>
                        <Spinner className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                        <span>{t('async_operations.saving')}</span>
                      </>
                    ) : isSuccess ? (
                      <span>{t('status.saved')}</span>
                    ) : (
                      <span>{saveText || t('crud_buttons.save')}</span>
                    )}
                  </Button>
                </>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
